import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import logo from '../../logo.png';
import { Star, Filter, Download, Phone, X, ChevronRight, Award, ClipboardList, Tag, Lock } from 'lucide-react';

export default function BancoTalento() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const puedeAcceder = ["superadmin","coordinador_general"].includes(user?.rol);

  const [talentos, setTalentos] = useState([]);
  const [talentosFiltrados, setTalentosFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [perfilSeleccionado, setPerfilSeleccionado] = useState(null);
  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  const [tabDetalle, setTabDetalle] = useState('info');
  const [documentos, setDocumentos] = useState([]);
  const [formDocumento, setFormDocumento] = useState({ tipo_documento: '', nombre_archivo: '', archivo_url: '', descripcion: '' });
  const [mostrarFormDoc, setMostrarFormDoc] = useState(false);
  const [guardandoDoc, setGuardandoDoc] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [filtroEstatus, setFiltroEstatus] = useState('');

  const [filtros, setFiltros] = useState({
    area_interes: '', tipo_participacion: '', disponibilidad: '', ocupacion: ''
  });

  const [formSeguimiento, setFormSeguimiento] = useState({
    accion: '', resultado: '', observaciones: ''
  });

  const [formEvaluacion, setFormEvaluacion] = useState({
    score_tecnico: 0, score_territorial: 0, score_liderazgo: 0,
    score_confianza: 0, score_gobierno: 0, recomendacion: '', comentario: ''
  });

  const [formClasificacion, setFormClasificacion] = useState({
    clasificacion_principal: '', clasificacion_secundaria: ''
  });

  useEffect(() => {
    if (puedeAcceder) fetchTalentos();
    else setLoading(false);
  }, [puedeAcceder]);

  const fetchTalentos = async () => {
    try {
      setLoading(true);
      const res = await api.get('/talento');
      setTalentos(res.data.perfiles || []);
      setTalentosFiltrados(res.data.perfiles || []);
    } catch (error) {
      console.error('Error al cargar talentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const aplicarFiltros = (nuevosFiltros, nuevoEstatus) => {
    let resultado = [...talentos];
    if (nuevosFiltros.area_interes) resultado = resultado.filter(t => t.area_interes?.toLowerCase() === nuevosFiltros.area_interes.toLowerCase());
    if (nuevosFiltros.tipo_participacion) resultado = resultado.filter(t => t.tipo_participacion_interes?.toLowerCase() === nuevosFiltros.tipo_participacion.toLowerCase());
    if (nuevosFiltros.disponibilidad) resultado = resultado.filter(t => t.disponibilidad_servir?.toLowerCase() === nuevosFiltros.disponibilidad.toLowerCase());
    if (nuevosFiltros.ocupacion) resultado = resultado.filter(t => t.ocupacion_general?.toLowerCase() === nuevosFiltros.ocupacion.toLowerCase());
    if (nuevoEstatus) resultado = resultado.filter(t => t.estatus_talento === nuevoEstatus);
    setTalentosFiltrados(resultado);
  };

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    const nuevosFiltros = { ...filtros, [name]: value };
    setFiltros(nuevosFiltros);
    aplicarFiltros(nuevosFiltros, filtroEstatus);
  };

  const handleFiltroEstatus = (est) => {
    const nuevo = filtroEstatus === est ? '' : est;
    setFiltroEstatus(nuevo);
    aplicarFiltros(filtros, nuevo);
  };

  const resetFiltros = () => {
    const vacio = { area_interes: '', tipo_participacion: '', disponibilidad: '', ocupacion: '' };
    setFiltros(vacio);
    setFiltroEstatus('');
    setTalentosFiltrados(talentos);
  };

  const descargarCSV = () => {
    if (talentosFiltrados.length === 0) return;
    const headers = ['Nombre', 'Teléfono', 'Ocupación', 'Área de Interés', 'Disponibilidad', 'Estatus'];
    const rows = talentosFiltrados.map(t => [
      `${t.nombre} ${t.apellido_paterno} ${t.apellido_materno || ''}`,
      t.telefono_principal, t.ocupacion_general, t.area_interes,
      t.disponibilidad_servir, t.estatus_talento
    ]);
    let csv = headers.join(',') + '\n';
    rows.forEach(row => { csv += row.map(cell => `"${cell || ''}"`).join(',') + '\n'; });
    const link = document.createElement('a');
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    link.download = `banco_talento_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleVerDetalle = async (talento) => {
    try {
      const res = await api.get(`/talento/${talento.id}`);
      setPerfilSeleccionado(res.data);
      setTabDetalle('info');
      setMostrarDetalle(true);
      setMensaje(null);
      // Cargar documentos
      try {
        const docRes = await api.get(`/talento/${talento.id}/documentos`);
        setDocumentos(docRes.data.documentos || []);
      } catch { setDocumentos([]); }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleActualizarEstatus = async (nuevoEstatus) => {
    try {
      await api.put(`/talento/${perfilSeleccionado.perfil.id}/estatus`, { estatus_talento: nuevoEstatus });
      setMensaje({ tipo: 'exito', texto: 'Estatus actualizado.' });
      const res = await api.get(`/talento/${perfilSeleccionado.perfil.id}`);
      setPerfilSeleccionado(res.data);
      fetchTalentos();
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al actualizar.' });
    }
  };

  const handleGuardarSeguimiento = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/talento/${perfilSeleccionado.perfil.id}/seguimiento`, formSeguimiento);
      setMensaje({ tipo: 'exito', texto: 'Seguimiento agregado.' });
      setFormSeguimiento({ accion: '', resultado: '', observaciones: '' });
      const res = await api.get(`/talento/${perfilSeleccionado.perfil.id}`);
      setPerfilSeleccionado(res.data);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al guardar.' });
    }
  };

  const handleGuardarEvaluacion = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/talento/${perfilSeleccionado.perfil.id}/evaluaciones`, formEvaluacion);
      setMensaje({ tipo: 'exito', texto: 'Evaluación guardada.' });
      setFormEvaluacion({ score_tecnico: 0, score_territorial: 0, score_liderazgo: 0, score_confianza: 0, score_gobierno: 0, recomendacion: '', comentario: '' });
      const res = await api.get(`/talento/${perfilSeleccionado.perfil.id}`);
      setPerfilSeleccionado(res.data);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al guardar.' });
    }
  };

  const handleGuardarClasificacion = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/talento/${perfilSeleccionado.perfil.id}/clasificacion`, formClasificacion);
      setMensaje({ tipo: 'exito', texto: 'Clasificación guardada.' });
      const res = await api.get(`/talento/${perfilSeleccionado.perfil.id}`);
      setPerfilSeleccionado(res.data);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al guardar.' });
    }
  };

  const handleGuardarDocumento = async (e) => {
    e.preventDefault();
    if (!formDocumento.tipo_documento || !formDocumento.nombre_archivo) return;
    setGuardandoDoc(true);
    try {
      await api.post(`/talento/${perfilSeleccionado.perfil.id}/documentos`, formDocumento);
      setMensaje({ tipo: 'exito', texto: '✅ Documento registrado.' });
      setFormDocumento({ tipo_documento: '', nombre_archivo: '', archivo_url: '', descripcion: '' });
      setMostrarFormDoc(false);
      const docRes = await api.get(`/talento/${perfilSeleccionado.perfil.id}/documentos`);
      setDocumentos(docRes.data.documentos || []);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al guardar documento.' });
    } finally {
      setGuardandoDoc(false);
    }
  };

  const handleEliminarDocumento = async (docId) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try {
      await api.delete(`/talento/documentos/${docId}`);
      const docRes = await api.get(`/talento/${perfilSeleccionado.perfil.id}/documentos`);
      setDocumentos(docRes.data.documentos || []);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error al eliminar.' });
    }
  };

  const handleSolicitarCV = async () => {
    try {
      await api.post(`/talento/${perfilSeleccionado.perfil.id}/seguimiento`, {
        accion: 'solicitud_cv',
        resultado: 'pendiente',
        observaciones: 'CV solicitado — pendiente de recibir'
      });
      setMensaje({ tipo: 'exito', texto: '📄 CV marcado como solicitado. Aparece en Seguimiento.' });
      const res = await api.get(`/talento/${perfilSeleccionado.perfil.id}`);
      setPerfilSeleccionado(res.data);
      setTimeout(() => setMensaje(null), 3000);
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error.' });
    }
  };

  const getEstatusColor = (estatus) => {
    switch (estatus) {
      case 'basico':      return 'bg-gray-100 text-gray-700';
      case 'por_ampliar': return 'bg-yellow-100 text-yellow-700';
      case 'evaluado':    return 'bg-blue-100 text-blue-700';
      case 'estrategico': return 'bg-purple-100 text-purple-700';
      case 'descartado':  return 'bg-red-100 text-red-700';
      case 'reserva':     return 'bg-orange-100 text-orange-700';
      default:            return 'bg-gray-100 text-gray-700';
    }
  };

  const scorePromedio = (eval_) => {
    const scores = [eval_.score_tecnico, eval_.score_territorial, eval_.score_liderazgo, eval_.score_confianza, eval_.score_gobierno].filter(s => s !== null);
    if (scores.length === 0) return 0;
    return (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
  };

  // ── Acceso denegado ──
  if (!puedeAcceder) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
          style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
          <img src={logo} alt="DOMINIUM" className="h-16" />
          <button onClick={() => navigate('/dashboard')}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
            ← Volver al Dashboard
          </button>
        </header>
        <div className="flex flex-col items-center justify-center min-h-[70vh]">
          <Lock className="text-gray-300 mb-4" size={56} />
          <p className="text-xl font-semibold text-gray-500">Acceso restringido</p>
          <p className="text-gray-400 text-sm mt-2">Solo el Super Administrador puede ver el Banco de Talento.</p>
          <button onClick={() => navigate('/dashboard')}
            className="mt-6 px-6 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 transition"
            style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-500">Cargando banco de talento...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
        style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
        <img src={logo} alt="DOMINIUM" className="h-16" />
        <button onClick={() => navigate('/dashboard')}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
          ← Volver al Dashboard
        </button>
      </header>

      <main className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Star className="text-yellow-500" size={28} />
            Banco de Talento Zacateco
          </h2>
          <p className="text-gray-500 text-sm">Perfiles con potencial para servir a Zacatlán — seguimiento y evaluación</p>
        </div>

        {/* CONTADORES POR ESTATUS — clickeables para filtrar */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
          {['basico', 'por_ampliar', 'evaluado', 'estrategico', 'reserva', 'descartado'].map(est => (
            <div key={est}
              onClick={() => handleFiltroEstatus(est)}
              className={`bg-white rounded-xl shadow-sm p-3 text-center cursor-pointer border-2 transition ${
                filtroEstatus === est ? 'border-green-500 bg-green-50' : 'border-transparent hover:border-gray-300'
              }`}>
              <p className="text-xs text-gray-500 capitalize">{est.replace('_', ' ')}</p>
              <p className="text-xl font-bold text-gray-800">{talentos.filter(p => p.estatus_talento === est).length}</p>
            </div>
          ))}
        </div>

        {/* FILTROS */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={18} className="text-gray-600" />
            <h3 className="font-semibold text-gray-800">Filtros de búsqueda</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Área de Interés</label>
              <select name="area_interes" value={filtros.area_interes} onChange={handleFiltroChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">Todas</option>
                <option value="educacion">Educación</option>
                <option value="salud">Salud</option>
                <option value="campo">Campo</option>
                <option value="turismo">Turismo</option>
                <option value="comercio">Comercio</option>
                <option value="juventud">Juventud</option>
                <option value="deporte">Deporte</option>
                <option value="cultura">Cultura</option>
                <option value="desarrollo_social">Desarrollo Social</option>
                <option value="obras_servicios">Obras y Servicios</option>
                <option value="seguridad">Seguridad</option>
                <option value="administracion">Administración</option>
                <option value="juridico">Jurídico</option>
                <option value="comunicacion">Comunicación</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Participación</label>
              <select name="tipo_participacion" value={filtros.tipo_participacion} onChange={handleFiltroChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">Todas</option>
                <option value="gobierno_municipal">Gobierno municipal</option>
                <option value="proyectos_comunitarios">Proyectos comunitarios</option>
                <option value="apoyo_tecnico">Apoyo técnico</option>
                <option value="organizacion_territorial">Organización territorial</option>
                <option value="voluntariado">Voluntariado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Disponibilidad</label>
              <select name="disponibilidad" value={filtros.disponibilidad} onChange={handleFiltroChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">Todas</option>
                <option value="si">Sí</option>
                <option value="tal_vez">Tal vez</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ocupación</label>
              <select name="ocupacion" value={filtros.ocupacion} onChange={handleFiltroChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">Todas</option>
                <option value="comerciante">Comerciante</option>
                <option value="docente">Docente</option>
                <option value="estudiante">Estudiante</option>
                <option value="profesionista">Profesionista</option>
                <option value="ama_de_casa">Ama de casa</option>
                <option value="campesino">Campesino</option>
                <option value="transportista">Transportista</option>
                <option value="servidor_publico">Servidor público</option>
                <option value="empleado_privado">Empleado privado</option>
                <option value="empresario">Empresario</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={resetFiltros}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition">
              Limpiar filtros
            </button>
            <button onClick={descargarCSV}
              className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition flex items-center gap-2">
              <Download size={16} /> Descargar CSV
            </button>
          </div>
        </div>

        {/* CONTADOR */}
        <div className="mb-4 text-sm text-gray-600">
          Mostrando <span className="font-semibold">{talentosFiltrados.length}</span> de <span className="font-semibold">{talentos.length}</span> perfiles
        </div>

        {/* TARJETAS */}
        {talentosFiltrados.length === 0 ? (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 text-center">
            <Star className="text-blue-400 mx-auto mb-2" size={32} />
            <p className="text-blue-800 font-medium">No se encontraron perfiles con estos filtros</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {talentosFiltrados.map((talento) => (
              <div key={talento.id}
                onClick={() => handleVerDetalle(talento)}
                className="bg-white rounded-xl shadow-sm p-5 border-t-4 border-yellow-400 hover:shadow-md transition cursor-pointer">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-800">
                      {talento.nombre} {talento.apellido_paterno} {talento.apellido_materno}
                    </h3>
                    <p className="text-sm text-gray-500 capitalize">{talento.ocupacion_general}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEstatusColor(talento.estatus_talento)}`}>
                    {talento.estatus_talento?.replace('_', ' ') || '⭐ talento'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                  <div className="bg-green-50 p-2 rounded">
                    <p className="text-gray-500">Disponibilidad</p>
                    <p className="font-medium capitalize">{talento.disponibilidad_servir || '—'}</p>
                  </div>
                  <div className="bg-blue-50 p-2 rounded">
                    <p className="text-gray-500">Área</p>
                    <p className="font-medium">{talento.area_interes || '—'}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center gap-1"><Phone size={12} /> {talento.telefono_principal}</span>
                  <span className="flex items-center gap-1 text-green-600 font-medium">
                    Ver perfil <ChevronRight size={14} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-gray-400 text-xs mt-8">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>
      </main>

      {/* ══════════════════════════════════════════
          MODAL DETALLE COMPLETO
      ══════════════════════════════════════════ */}
      {mostrarDetalle && perfilSeleccionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

            {/* Header modal */}
            <div className="flex items-center justify-between p-6 sticky top-0 z-10"
              style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)', borderRadius: '1rem 1rem 0 0' }}>
              <div>
                <h3 className="text-white font-bold text-lg">
                  {perfilSeleccionado.perfil.nombre} {perfilSeleccionado.perfil.apellido_paterno}
                </h3>
                <p className="text-white text-opacity-80 text-sm capitalize">{perfilSeleccionado.perfil.ocupacion_general}</p>
              </div>
              <div className="flex items-center gap-2">
                {/* WhatsApp */}
                <button
                  onClick={() => {
                    const tel = perfilSeleccionado.perfil.telefono_principal?.replace(/\D/g, '');
                    const msg = encodeURIComponent(`Hola ${perfilSeleccionado.perfil.nombre}, te contacto desde DOMINIUM - Banco de Talento Zacateco.`);
                    window.open(`https://wa.me/52${tel}?text=${msg}`, '_blank');
                  }}
                  className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-lg text-xs font-medium transition flex items-center gap-1">
                  📱 WhatsApp
                </button>
                <button onClick={() => { setMostrarDetalle(false); setMensaje(null); }}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-1 rounded transition">
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* TABS */}
            <div className="flex border-b px-6 overflow-x-auto">
              {[
                { key: 'info',          label: '📋 Información' },
                { key: 'seguimiento',   label: '📌 Seguimiento' },
                { key: 'evaluacion',    label: '⭐ Evaluación' },
                { key: 'clasificacion', label: '🏷 Clasificación' },
                { key: 'documentos',    label: '📁 Documentos' }
              ].map(t => (
                <button key={t.key}
                  onClick={() => { setTabDetalle(t.key); setMensaje(null); }}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                    tabDetalle === t.key ? 'border-green-600 text-green-600' : 'border-transparent text-gray-600 hover:text-gray-800'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>

            <div className="p-6">
              {mensaje && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${
                  mensaje.tipo === 'exito' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'
                }`}>{mensaje.texto}</div>
              )}

              {/* ── TAB INFORMACIÓN ── */}
              {tabDetalle === 'info' && (
                <div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Teléfono</p>
                      <p className="font-medium">{perfilSeleccionado.perfil.telefono_principal}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Sección</p>
                      <p className="font-medium">{perfilSeleccionado.perfil.numero_seccion || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Área de Interés</p>
                      <p className="font-medium capitalize">{perfilSeleccionado.perfil.area_interes || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Disponibilidad</p>
                      <p className="font-medium capitalize">{perfilSeleccionado.perfil.disponibilidad_servir || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Tipo Participación</p>
                      <p className="font-medium capitalize">{perfilSeleccionado.perfil.tipo_participacion_interes?.replace('_', ' ') || '—'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Origen</p>
                      {(() => {
                        const ORIGENES = {
                          automatico_por_estructura: '🏛 Automático por estructura',
                          sugerido_en_campo:         '📍 Sugerido en campo',
                          recomendacion_politica:    '🤝 Recomendación política',
                          convocatoria:              '📢 Convocatoria',
                          evento:                    '🎪 Evento',
                          reclutamiento_directo:     '🎯 Reclutamiento directo',
                        };
                        return <p className="font-medium">{ORIGENES[perfilSeleccionado.perfil.origen] || perfilSeleccionado.perfil.origen?.replace(/_/g,' ') || '—'}</p>;
                      })()}
                    </div>
                    {perfilSeleccionado.perfil.domicilio_breve && (
                      <div className="bg-gray-50 p-3 rounded-lg col-span-2">
                        <p className="text-xs text-gray-500">Domicilio</p>
                        <p className="font-medium">{perfilSeleccionado.perfil.domicilio_breve}</p>
                      </div>
                    )}
                  </div>

                  {/* Cambiar estatus */}
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Cambiar Estatus:</p>
                    <div className="flex flex-wrap gap-2">
                      {['basico', 'por_ampliar', 'evaluado', 'estrategico', 'reserva', 'descartado'].map(est => (
                        <button key={est} onClick={() => handleActualizarEstatus(est)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                            perfilSeleccionado.perfil.estatus_talento === est
                              ? 'ring-2 ring-green-500 ' + getEstatusColor(est)
                              : getEstatusColor(est) + ' opacity-60 hover:opacity-100'
                          }`}>
                          {est.replace('_', ' ')}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Cambiar origen */}
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Origen del Talento:</p>
                    <select
                      defaultValue={perfilSeleccionado.perfil.origen || ''}
                      onChange={async (e) => {
                        try {
                          await api.put(`/talento/${perfilSeleccionado.perfil.id}/estatus`, {
                            origen: e.target.value
                          });
                          const res = await api.get(`/talento/${perfilSeleccionado.perfil.id}`);
                          setPerfilSeleccionado(res.data);
                        } catch(err) { console.error(err); }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="automatico_por_estructura">🏛 Automático por estructura</option>
                      <option value="sugerido_en_campo">📍 Sugerido en campo</option>
                      <option value="recomendacion_politica">🤝 Recomendación política</option>
                      <option value="convocatoria">📢 Convocatoria</option>
                      <option value="evento">🎪 Evento</option>
                      <option value="reclutamiento_directo">🎯 Reclutamiento directo</option>
                    </select>
                  </div>

                  {perfilSeleccionado.clasificacion && (
                    <div className="bg-purple-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500 mb-1">Clasificación actual</p>
                      <p className="font-medium text-purple-800">{perfilSeleccionado.clasificacion.clasificacion_principal}</p>
                      {perfilSeleccionado.clasificacion.clasificacion_secundaria && (
                        <p className="text-sm text-purple-600">{perfilSeleccionado.clasificacion.clasificacion_secundaria}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── TAB SEGUIMIENTO ── */}
              {tabDetalle === 'seguimiento' && (
                <div>
                  <form onSubmit={handleGuardarSeguimiento} className="bg-gray-50 p-4 rounded-lg mb-4 space-y-3">
                    <h5 className="font-semibold text-gray-700">Agregar Seguimiento</h5>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Acción realizada *</label>
                      <select value={formSeguimiento.accion} onChange={(e) => setFormSeguimiento({...formSeguimiento, accion: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" required>
                        <option value="">Seleccionar...</option>
                        <option value="llamada_telefonica">📞 Llamada telefónica</option>
                        <option value="entrevista">🤝 Entrevista</option>
                        <option value="solicitud_cv">📄 Solicitud de CV</option>
                        <option value="visita_domicilio">🏠 Visita a domicilio</option>
                        <option value="reunion_presencial">👥 Reunión presencial</option>
                        <option value="mensaje_whatsapp">📱 Mensaje WhatsApp</option>
                        <option value="evaluacion_aplicada">⭐ Evaluación aplicada</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Resultado</label>
                      <input value={formSeguimiento.resultado} onChange={(e) => setFormSeguimiento({...formSeguimiento, resultado: e.target.value})}
                        placeholder="Ej: Interesado, pendiente de confirmar..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Observaciones</label>
                      <textarea value={formSeguimiento.observaciones} onChange={(e) => setFormSeguimiento({...formSeguimiento, observaciones: e.target.value})}
                        rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <button type="submit"
                      className="w-full text-white font-semibold py-2 rounded-lg hover:opacity-90 transition text-sm"
                      style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                      Guardar Seguimiento
                    </button>
                  </form>
                  <h5 className="font-semibold text-gray-700 mb-2">Historial ({perfilSeleccionado.seguimiento?.length || 0})</h5>
                  {!perfilSeleccionado.seguimiento?.length ? (
                    <p className="text-gray-400 text-sm text-center py-4">Sin seguimientos registrados</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {perfilSeleccionado.seguimiento.map((s, i) => (
                        <div key={i} className="bg-white border rounded-lg p-3">
                          <div className="flex justify-between items-start">
                            <p className="text-sm font-medium capitalize">{s.accion.replace('_', ' ')}</p>
                            <p className="text-xs text-gray-400">{new Date(s.fecha).toLocaleDateString('es-MX')}</p>
                          </div>
                          {s.resultado && <p className="text-xs text-gray-600 mt-1">✅ {s.resultado}</p>}
                          {s.observaciones && <p className="text-xs text-gray-500 mt-1">{s.observaciones}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── TAB EVALUACIÓN ── */}
              {tabDetalle === 'evaluacion' && (
                <div>
                  <form onSubmit={handleGuardarEvaluacion} className="bg-gray-50 p-4 rounded-lg mb-4 space-y-3">
                    <h5 className="font-semibold text-gray-700">Nueva Evaluación (0-10)</h5>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { key: 'score_tecnico',      label: '🔧 Técnico' },
                        { key: 'score_territorial',  label: '🗺 Territorial' },
                        { key: 'score_liderazgo',    label: '👑 Liderazgo' },
                        { key: 'score_confianza',    label: '🤝 Confianza' },
                        { key: 'score_gobierno',     label: '🏛 Gobierno' }
                      ].map(s => (
                        <div key={s.key}>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{s.label}</label>
                          <input type="number" min="0" max="10"
                            value={formEvaluacion[s.key]}
                            onChange={(e) => setFormEvaluacion({...formEvaluacion, [s.key]: parseInt(e.target.value) || 0})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                        </div>
                      ))}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Recomendación</label>
                      <select value={formEvaluacion.recomendacion} onChange={(e) => setFormEvaluacion({...formEvaluacion, recomendacion: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                        <option value="">Seleccionar...</option>
                        <option value="perfil_estrategico">⭐ Perfil estratégico</option>
                        <option value="seguir_cultivando">🌱 Seguir cultivando</option>
                        <option value="cargo_operativo">⚙️ Cargo operativo</option>
                        <option value="cargo_gobierno">🏛 Cargo de gobierno</option>
                        <option value="no_viable">❌ No viable</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Comentario</label>
                      <textarea value={formEvaluacion.comentario} onChange={(e) => setFormEvaluacion({...formEvaluacion, comentario: e.target.value})}
                        rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <button type="submit"
                      className="w-full text-white font-semibold py-2 rounded-lg hover:opacity-90 transition text-sm"
                      style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                      Guardar Evaluación
                    </button>
                  </form>
                  <h5 className="font-semibold text-gray-700 mb-2">Evaluaciones previas ({perfilSeleccionado.evaluaciones?.length || 0})</h5>
                  {!perfilSeleccionado.evaluaciones?.length ? (
                    <p className="text-gray-400 text-sm text-center py-4">Sin evaluaciones registradas</p>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {perfilSeleccionado.evaluaciones.map((e, i) => (
                        <div key={i} className="bg-white border rounded-lg p-3">
                          <div className="flex justify-between items-start mb-2">
                            <p className="text-sm font-medium">Promedio: {scorePromedio(e)}/10</p>
                            <p className="text-xs text-gray-400">{new Date(e.fecha_evaluacion).toLocaleDateString('es-MX')}</p>
                          </div>
                          <div className="grid grid-cols-5 gap-1 text-xs text-center mb-2">
                            {[
                              { label: 'Téc', val: e.score_tecnico },
                              { label: 'Ter', val: e.score_territorial },
                              { label: 'Lid', val: e.score_liderazgo },
                              { label: 'Con', val: e.score_confianza },
                              { label: 'Gob', val: e.score_gobierno }
                            ].map((s, j) => (
                              <div key={j} className="bg-gray-50 rounded p-1">
                                <p className="text-gray-500">{s.label}</p>
                                <p className="font-bold">{s.val ?? '—'}</p>
                              </div>
                            ))}
                          </div>
                          {e.recomendacion && <p className="text-xs text-green-700 font-medium capitalize">{e.recomendacion.replace('_', ' ')}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── TAB CLASIFICACIÓN ── */}
              {tabDetalle === 'clasificacion' && (
                <div>
                  {perfilSeleccionado.clasificacion && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                      <p className="text-xs text-gray-500 mb-1">Clasificación actual</p>
                      <p className="font-bold text-purple-800 text-lg">{perfilSeleccionado.clasificacion.clasificacion_principal}</p>
                      {perfilSeleccionado.clasificacion.clasificacion_secundaria && (
                        <p className="text-sm text-purple-600">{perfilSeleccionado.clasificacion.clasificacion_secundaria}</p>
                      )}
                    </div>
                  )}
                  <form onSubmit={handleGuardarClasificacion} className="bg-gray-50 p-4 rounded-lg space-y-3">
                    <h5 className="font-semibold text-gray-700">Nueva Clasificación</h5>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Clasificación Principal *</label>
                      <select value={formClasificacion.clasificacion_principal} onChange={(e) => setFormClasificacion({...formClasificacion, clasificacion_principal: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" required>
                        <option value="">Seleccionar...</option>
                        <option value="cuadro_territorial">🗺 Cuadro territorial</option>
                        <option value="perfil_tecnico">🔧 Perfil técnico</option>
                        <option value="perfil_administrativo">📋 Perfil administrativo</option>
                        <option value="perfil_operativo">⚙️ Perfil operativo</option>
                        <option value="enlace_sectorial">🔗 Enlace sectorial</option>
                        <option value="liderazgo_social">👑 Liderazgo social</option>
                        <option value="perfil_gobierno_medio">🏛 Perfil de gobierno medio</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Clasificación Secundaria</label>
                      <input value={formClasificacion.clasificacion_secundaria} onChange={(e) => setFormClasificacion({...formClasificacion, clasificacion_secundaria: e.target.value})}
                        placeholder="Opcional..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <button type="submit"
                      className="w-full text-white font-semibold py-2 rounded-lg hover:opacity-90 transition text-sm"
                      style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                      Guardar Clasificación
                    </button>
                  </form>
                </div>
              )}

              {/* ── PESTAÑA DOCUMENTOS ── */}
              {tabDetalle === 'documentos' && (
                <div className="p-4 space-y-4">

                  {/* Acciones rápidas 13.12 PDF */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <button onClick={handleSolicitarCV}
                      className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 transition text-sm font-medium">
                      📄 Solicitar CV
                    </button>
                    <button onClick={() => {
                      setFormDocumento({ tipo_documento: 'entrevista', nombre_archivo: `Entrevista ${new Date().toLocaleDateString('es-MX')}`, archivo_url: '', descripcion: '' });
                      setMostrarFormDoc(true);
                    }}
                      className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-purple-300 text-purple-700 bg-purple-50 hover:bg-purple-100 transition text-sm font-medium">
                      🤝 Registrar Entrevista
                    </button>
                    <button onClick={() => { setFormDocumento({ tipo_documento: '', nombre_archivo: '', archivo_url: '', descripcion: '' }); setMostrarFormDoc(true); }}
                      className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-green-300 text-green-700 bg-green-50 hover:bg-green-100 transition text-sm font-medium">
                      ➕ Cargar Documento
                    </button>
                  </div>

                  {/* Formulario de documento */}
                  {mostrarFormDoc && (
                    <form onSubmit={handleGuardarDocumento} className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-3">
                      <h5 className="font-semibold text-gray-700 text-sm">Registrar Documento</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Tipo *</label>
                          <select value={formDocumento.tipo_documento}
                            onChange={(e) => setFormDocumento({...formDocumento, tipo_documento: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" required>
                            <option value="">Seleccionar...</option>
                            <option value="curriculum">📄 Currículum Vitae (CV)</option>
                            <option value="titulo_profesional">🎓 Título Profesional</option>
                            <option value="cedula_profesional">📋 Cédula Profesional</option>
                            <option value="carta_recomendacion">✉️ Carta de Recomendación</option>
                            <option value="identificacion">🪪 Identificación Oficial</option>
                            <option value="entrevista">🤝 Registro de Entrevista</option>
                            <option value="evaluacion_externa">⭐ Evaluación Externa</option>
                            <option value="otro">📎 Otro</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Nombre / Descripción corta *</label>
                          <input value={formDocumento.nombre_archivo}
                            onChange={(e) => setFormDocumento({...formDocumento, nombre_archivo: e.target.value})}
                            placeholder="Ej: CV_Juan_2026.pdf"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" required />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">URL del documento (Google Drive, Dropbox, etc.)</label>
                          <input value={formDocumento.archivo_url}
                            onChange={(e) => setFormDocumento({...formDocumento, archivo_url: e.target.value})}
                            placeholder="https://drive.google.com/..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Notas</label>
                          <textarea value={formDocumento.descripcion}
                            onChange={(e) => setFormDocumento({...formDocumento, descripcion: e.target.value})}
                            rows={2} placeholder="Observaciones opcionales..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setMostrarFormDoc(false)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-gray-600 text-sm hover:bg-gray-50 transition">
                          Cancelar
                        </button>
                        <button type="submit" disabled={guardandoDoc}
                          className="flex-1 text-white font-semibold py-2 rounded-lg hover:opacity-90 transition text-sm disabled:opacity-50"
                          style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
                          {guardandoDoc ? 'Guardando...' : 'Registrar'}
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Lista de documentos */}
                  {documentos.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <p className="text-3xl mb-2">📁</p>
                      <p className="text-sm">No hay documentos registrados</p>
                      <p className="text-xs mt-1">Usa los botones de arriba para agregar</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {documentos.map((doc, i) => {
                        const TIPO_ICONS = {
                          curriculum: '📄', titulo_profesional: '🎓', cedula_profesional: '📋',
                          carta_recomendacion: '✉️', identificacion: '🪪', entrevista: '🤝',
                          evaluacion_externa: '⭐', otro: '📎'
                        };
                        return (
                          <div key={i} className="flex items-start justify-between p-3 bg-gray-50 rounded-xl border border-gray-200">
                            <div className="flex items-start gap-3">
                              <span className="text-xl">{TIPO_ICONS[doc.tipo_documento] || '📎'}</span>
                              <div>
                                <p className="font-medium text-sm text-gray-800">{doc.nombre_archivo}</p>
                                <p className="text-xs text-gray-500 capitalize">{doc.tipo_documento?.replace(/_/g, ' ')}</p>
                                {doc.descripcion && <p className="text-xs text-gray-400 mt-0.5">{doc.descripcion}</p>}
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {new Date(doc.fecha_subida).toLocaleDateString('es-MX')}
                                  {doc.subido_nombre && ` · ${doc.subido_nombre} ${doc.subido_apellido}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-2">
                              {doc.validado && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✓ Validado</span>}
                              {doc.archivo_url && (
                                <a href={doc.archivo_url} target="_blank" rel="noreferrer"
                                  className="text-xs text-blue-600 hover:underline font-medium">Ver</a>
                              )}
                              <button onClick={() => handleEliminarDocumento(doc.id)}
                                className="text-red-400 hover:text-red-600 transition text-xs">✕</button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
